import { getIn, useFormik } from "formik";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { getYearlyRecapImage, updateYearlyRecap } from "../api";
import useValidations from "../../../../utils/hooks/useValidations";

const useAddYearlyRecap = ({
  refetch,
  yearlyRecapState,
  handleModal,
  setYearlyRecapState,
}) => {
  const imageRef = useRef(null);
  const aliasImageRef = useRef(null);
  const { arabicRegex } = useValidations();
  const [imageUploadState, setImageUploadState] = useState({
    imageLoading: false,
    imageAliasLoading: false,
  });
  const validationSchema = Yup.object({
    content_type: Yup.mixed().required("Content type is required"),
    headings: Yup.array().when("content_type", {
      is: (val) => val !== 2,
      then: () =>
        Yup.array()
          .of(
            Yup.object({
              heading: Yup.string().required("Heading is required"),
              alias_heading: Yup.string()
                .required("Heading is required")
                .matches(arabicRegex, "Enter in arabic letters"),
              size: Yup.string().required("Size is required"),
              color: Yup.string().required("Color is required"),
            })
          )
          .min(1, "At least one heading is required")
          .required("Heading is required"),
      otherwise: () => Yup.array().notRequired(),
    }),
    heading_position: Yup.string().when("content_type", {
      is: 1,
      then: (schema) => schema.required("Heading position is required"),
      otherwise: (schema) => schema,
    }),
    dynamic_type: Yup.string().when("content_type", {
      is: 2,
      then: (schema) => schema.required("Dynamic type is required"),
      otherwise: (schema) => schema,
    }),
    is_button: Yup.number(),
    button_color: Yup.string().when("is_button", {
      is: 1,
      then: (schema) => schema.required("Button color is required"),
      otherwise: (schema) => schema,
    }),
    button_text: Yup.string().when("is_button", {
      is: 1,
      then: (schema) => schema.required("Button text is required"),
      otherwise: (schema) => schema,
    }),
    button_text_color: Yup.string().when("is_button", {
      is: 1,
      then: (schema) => schema.required("Button text color is required"),
      otherwise: (schema) => schema,
    }),
    alias_button_text: Yup.string().when("is_button", {
      is: 1,
      then: (schema) =>
        schema
          .required("Button text is required")
          .matches(arabicRegex, "Enter in arabic letters"),
      otherwise: (schema) => schema,
    }),
    image: Yup.string(),
    alias_image: Yup.string(),
    status: Yup.number(),
    position: Yup.number().required("Recap position is required"),
  });
  const formik = useFormik({
    initialValues: {
      content_type: yearlyRecapState?.itemData?.content_type ?? "",
      position: yearlyRecapState?.itemData?.position ?? "",
      headings: yearlyRecapState?.itemData?.headings?.length
        ? yearlyRecapState.itemData.headings
        : [
            {
              heading: "",
              alias_heading: "",
              size: "",
              color: "#000000",
            },
          ],
      heading_position: yearlyRecapState?.itemData?.heading_position ?? "",
      dynamic_type: yearlyRecapState?.itemData?.dynamic_type ?? "",
      is_button: yearlyRecapState?.itemData?.is_button ?? 1,
      button_color: yearlyRecapState?.itemData?.button_color ?? "#ff0000",
      button_text: yearlyRecapState?.itemData?.button_text ?? "",
      button_text_color:
        yearlyRecapState?.itemData?.button_text_color ?? "#ff0000",
      alias_button_text: yearlyRecapState?.itemData?.alias_button_text ?? "",
      image: yearlyRecapState?.itemData?.image || "",
      alias_image: yearlyRecapState?.itemData?.alias_image || "",
      status: yearlyRecapState?.itemData?.status ?? 1,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      const data = {
        ...values,
        heading_position:
          values.content_type === 1 ? values.heading_position : "",
        dynamic_type: values.content_type === 2 ? values.dynamic_type : "",
        headings: !(
          formik?.values?.content_type?.toString() === "2" &&
          ["product", "store"].includes(formik?.values?.dynamic_type)
        )
          ? values.headings
          : [],
        button_color: values.is_button === 1 ? values.button_color : "",
        button_text: values.is_button === 1 ? values.button_text : "",
        button_text_color:
          values.is_button === 1 ? values.button_text_color : "",
        alias_button_text:
          values.is_button === 1 ? values.alias_button_text : "",
        ...(yearlyRecapState?.isEdit && {
          id: yearlyRecapState?.itemId,
        }),
      };
      updateYearlyRecap(data).then((response) => {
        if (response?.data?.success) {
          toast.success(
            response?.data?.data ?? "Yearly recap icon Updated successfully"
          );
          refetch();
          handleModal();
          setSubmitting(false);
        } else if (response?.data?.status_code === 422) {
          let errors = response?.data?.errors;
          let errorFields = Object.keys(errors);
          errorFields?.forEach((field) => {
            formik.setFieldError(field, errors[field]);
          });
          setSubmitting(false);
        } else if (response?.success === false) {
          let errors = response?.message;
          let errorFields = Object.keys(errors);
          errorFields?.forEach((field) => {
            formik.setFieldError(field, errors[field]);
          });
          setSubmitting(false);
        } else {
          toast.error(response?.data?.message ?? "Something went wrong");
          setSubmitting(false);
        }
      });
    },
  });
  const addHeading = () => {
    formik.setValues({
      ...formik.values,
      headings: [
        ...formik.values.headings,
        {
          heading: "",
          alias_heading: "",
          size: "",
          color: "#000000",
        },
      ],
    });
  };
  const handleImageChange = async (event, fieldName, ref) => {
    const selectedLogo = event?.target?.files[0];
    if (selectedLogo) {
      if (
        !["image/jpeg", "image/png", "image/jpg"].includes(selectedLogo.type)
      ) {
        formik.setFieldError(
          `${fieldName === "image" ? "image" : "alias_image"}`,
          "Only image files are allowed"
        );
        return;
      }
      const maxSize = 3 * 1024 * 1024;
      if (selectedLogo.size > maxSize) {
        formik.setFieldError(
          `${fieldName === "image" ? "image" : "alias_image"}`,
          "Image size should be less than 3MB"
        );
        return;
      }
      // File type validation
      setImageUploadState((prev) => ({
        ...prev,
        ...(fieldName === "image"
          ? { imageLoading: true }
          : { imageAliasLoading: true }),
      }));

      const formData = new FormData();
      formData.append("image", selectedLogo);
      await getYearlyRecapImage(formData).then((res) => {
        if (res?.data?.status_code === 200) {
          formik.setFieldValue(fieldName, res?.data?.data?.image_path);
          if (fieldName === "image") {
            setImageUploadState((prev) => ({
              ...prev,
              imageLoading: false,
            }));
            setYearlyRecapState((prev) => ({
              ...prev,
              imageFile: URL.createObjectURL(selectedLogo) ?? "",
            }));
            if (ref?.current) ref.current.value = "";
          } else {
            setImageUploadState((prev) => ({
              ...prev,
              imageAliasLoading: false,
            }));
            setYearlyRecapState((prev) => ({
              ...prev,
              imageAliasFile: URL.createObjectURL(selectedLogo) ?? "",
            }));
            if (ref?.current) ref.current.value = "";
          }
        } else if (res?.status_code === 422 && res?.errors) {
          Object.keys(res?.errors).forEach((field) => {
            formik.setFieldError(`${fieldName}`, res?.errors[field]);
          });
          if (ref?.current) ref.current.value = "";
        } else {
          toast.error(`Unable to add image, Please try again.`);
          if (ref?.current) ref.current.value = "";
        }
      });
    }
  };
  const getFieldError = (fieldName) => {
    const error = getIn(formik.errors, fieldName);
    const touch = getIn(formik.touched, fieldName);

    if (touch && error) {
      return error;
    }

    return "";
  };
  const handleDelete = (index) => {
    formik.setValues({
      ...formik.values,
      headings: formik.values.headings.filter((item, i) => i !== index),
    });
  };

  return {
    formik,
    imageUploadState,
    imageRef,
    aliasImageRef,
    handleDelete,
    addHeading,
    getFieldError,
    handleImageChange,
  };
};

export default useAddYearlyRecap;
